/**
 * ReservoirMasterReportPage.tsx — Consolidated Master Report
 *
 * Single page that pulls all data from every reservoir-related page
 * (Diffuser Module, Module Array, Reservoir Layout, Bed Physics, Validation)
 * and generates ONE comprehensive PDF report containing everything.
 *
 * Pulls from localStorage (persistence) so any saved values from any page
 * are reflected here. Includes a season selector cascading into all calcs.
 */

import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  FileText, Download, ArrowLeft, CheckCircle2,
  Layers, MapPin, Wind, Shield, BarChart3, Mountain, Activity,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted, KEYS } from '@/lib/sessionPersistence';
import { generateMasterReservoirReportPDF } from '@/lib/reservoirPdf';
import { SEASONAL_PROFILES, getProfile, DEFAULT_PROFILE_ID } from '@/lib/seasonalProfiles';
import { toast } from 'sonner';

const FRAME_SIZE = 2.0;
const G = 9.81;
const P_ATM = 101_325;
const RHO_AIR_STD = 1.293;
const O2_MASS_FRAC = 0.2314;
const DO_SAT_20 = 9.09;
const GAMMA_AIR = 1.4;

// ─── ModSpec ──────────────────────────────────────────────────────────────────
interface ModSpec {
  diffuserCount: number; cornerOffsetM: number; mountingHeightM: number;
  diffuserType: string;  diffuserDiamMm: number; poreSizeUm: number;
  airflowPerDiffNm3h: number; diffuserDropKpa: number; waterDepthM: number;
  waterTempC: number; currentDoMgL: number; alphaFactor: number;
  betaFactor: number; blowerEff: number; safetyPct: number;
  tubeLengthM?: number; tubeCount?: number; tubeDiamMm?: number;
  tubePattern?: string; tubeCentreOffsetM?: number; porePerCm?: number;
}
const DEFAULTS: ModSpec = {
  diffuserCount: 8, cornerOffsetM: 0.35, mountingHeightM: 0.40,
  diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300,
  poreSizeUm: 80, airflowPerDiffNm3h: 1.2, diffuserDropKpa: 1.5,
  waterDepthM: 3.0, waterTempC: 12, currentDoMgL: 2.0,
  alphaFactor: 0.85, betaFactor: 0.98, blowerEff: 0.70, safetyPct: 10,
};

// ─── Physics helpers ──────────────────────────────────────────────────────────
function rhoW(T: number) { return 999.842 + 6.793952e-2*T - 9.095290e-3*T**2 + 1.001685e-4*T**3 - 1.120083e-6*T**4; }
function muW(T: number)  { return 2.414e-5 * Math.pow(10, 247.8 / (T + 133.15)); }
function nuW(T: number)  { return muW(T) / rhoW(T); }
function sigmaW(T: number) { return Math.max(0.04, 0.07564 - 1.376e-4 * T); }
function doSat(T: number) { return 468 / (31.6 + T); }
function bubbleDiamMm(poreSizeUm: number, qNm3h: number, pores: number, T: number) {
  const d_p = poreSizeUm / 1e6;
  const d_st = Math.pow((6 * sigmaW(T) * d_p) / (rhoW(T) * G), 1/3);
  const q_p = (qNm3h / 3600) / Math.max(1, pores);
  return d_st * (1 + 0.9 * Math.pow(Math.max(q_p * 1e9, 0.001), 0.25)) * 1000;
}
function riseVel(d_mm: number, T: number) {
  const v0 = d_mm <= 0.5 ? 0.13 + 0.16*d_mm
           : d_mm <= 3.0 ? 0.14 + 0.065*d_mm
           : d_mm <= 6.0 ? 0.335 + 0.02*(d_mm - 3) : 0.395;
  return v0 * Math.pow(nuW(20) / nuW(T), 0.7);
}
function sotePerM(d_mm: number) { return Math.max(1.5, Math.min(9.5, 10.5 * Math.exp(-0.32 * d_mm))); }
function blowerKw(nm3h: number, submM: number, dropKpa: number, safetyPct: number, eff: number) {
  const Q = nm3h / 3600;
  const P_out = P_ATM + (rhoW(20) * G * submM + dropKpa * 1000) * (1 + safetyPct / 100);
  const exp = (GAMMA_AIR - 1) / GAMMA_AIR;
  return (GAMMA_AIR / (GAMMA_AIR - 1)) * P_ATM * Q * (Math.pow(P_out / P_ATM, exp) - 1) / eff / 1000;
}
function unitsPerModule(spec: ModSpec): number {
  if (spec.diffuserType === 'hdpe_porous_tube' || spec.diffuserType === 'epdm_porous_tube')
    return Math.max(1, spec.tubeCount ?? 2);
  return spec.diffuserCount;
}
function poresPerUnit(spec: ModSpec): number {
  if (spec.diffuserType === 'hdpe_porous_tube' || spec.diffuserType === 'epdm_porous_tube')
    return Math.max(1, Math.round((spec.porePerCm ?? 8) * (spec.tubeLengthM ?? 1.8) * 100));
  const r = spec.diffuserDiamMm / 2;
  const density = spec.diffuserType === 'membrane_disc_ptfe' ? 0.40
                : spec.diffuserType === 'ceramic_dome'       ? 0.10
                                                              : 0.35;
  return Math.round(density * Math.PI * r * r);
}

// Reservoir constants (Barcombe)
const RESERVOIR_AREA_HA = 3.4;
const RESERVOIR_MEAN_DEPTH = 2.3;
const RESERVOIR_VOL_M3 = 78200;
const EFFECTIVE_AREA_HA = 2.3;

// Array zone definitions
const ZONE_DEFS = [
  { id: 'A', label: 'Deepest basin (NE)',         cx: 150, cy: 90,  depthM: 4.4 },
  { id: 'B', label: 'NW-central deep patch',      cx: 92,  cy: 88,  depthM: 3.5 },
  { id: 'C', label: 'Central-west deep',          cx: 62,  cy: 195, depthM: 3.3 },
  { id: 'D', label: 'SE central deep',            cx: 100, cy: 278, depthM: 3.2 },
  { id: 'E', label: 'East-central deep',          cx: 152, cy: 170, depthM: 3.1 },
  { id: 'F', label: 'Silt zone - north',          cx: 105, cy: 135, depthM: 2.5 },
  { id: 'G', label: 'Silt zone - middle',         cx: 115, cy: 230, depthM: 2.4 },
  { id: 'H', label: 'Silt zone - south',          cx: 92,  cy: 340, depthM: 2.3 },
];

export default function ReservoirMasterReportPage() {
  const navigate = useNavigate();
  const baseSpec = useMemo(() => loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS), []);
  const [seasonId, setSeasonId] = useState<string>(() =>
    loadPersisted<{ id: string }>(KEYS.BED_RECOMMENDATIONS + '__season', { id: DEFAULT_PROFILE_ID }).id ?? DEFAULT_PROFILE_ID);
  const profile = getProfile(seasonId);
  useEffect(() => { savePersisted(KEYS.BED_RECOMMENDATIONS + '__season', { id: seasonId }); }, [seasonId]);

  // Inject seasonal values
  const spec: ModSpec = useMemo(() => ({
    ...baseSpec,
    waterTempC: profile.waterTempC,
    currentDoMgL: profile.backgroundDOMgL,
    alphaFactor: profile.alphaFactor,
    betaFactor: profile.betaFactor,
  }), [baseSpec, profile]);

  // ─── Compute all values shared across the master report ────────────────
  const computed = useMemo(() => {
    const T = spec.waterTempC;
    const nUnits = unitsPerModule(spec);
    const poresEach = poresPerUnit(spec);
    const poresTotal = poresEach * nUnits;
    const qPerModule = spec.airflowPerDiffNm3h * nUnits;
    const d_b = bubbleDiamMm(spec.poreSizeUm, qPerModule, poresTotal, T);
    const v_rise = riseVel(d_b, T);
    const soteM = sotePerM(d_b);
    const effSubm = Math.max(0.5, spec.waterDepthM - spec.mountingHeightM);
    const sotrPerMod = qPerModule * RHO_AIR_STD * O2_MASS_FRAC * (soteM * effSubm / 100);
    // AOTR at CURRENT (pre-aeration) DO — shown on spec tables
    const aotrPerMod = sotrPerMod * spec.alphaFactor * spec.betaFactor
                       * Math.pow(1.024, T - 20)
                       * Math.max(0, doSat(T) - spec.currentDoMgL) / DO_SAT_20;
    // DESIGN AOTR at 4 mg/L target working DO — correct for SOD sizing
    // (Metcalf & Eddy 2014 §5.6)
    const TARGET_WORKING_DO_MGL = 4.0;
    const aotrDesign = sotrPerMod * spec.alphaFactor * spec.betaFactor
                       * Math.pow(1.024, T - 20)
                       * Math.max(0.5, doSat(T) - TARGET_WORKING_DO_MGL) / DO_SAT_20;
    const kwPerMod = blowerKw(qPerModule, effSubm, spec.diffuserDropKpa, spec.safetyPct, spec.blowerEff);

    // Plume radii
    const r_diff = spec.diffuserDiamMm / 2000;
    const r_inf_bed = Math.max(r_diff, 0.35 * effSubm);
    const r_surface = r_diff + 0.1 * effSubm;

    // Optimization (Wuest + SOD) — uses DESIGN AOTR
    const sodRate = profile.sodRateG_m2_d;
    const safetyF = 1.3;
    const target = 70;
    const totalSOD = (RESERVOIR_AREA_HA * 1e4 * sodRate) / (1000 * 24);
    const sodSafety = totalSOD * safetyF;
    const modsBySOD = Math.max(1, Math.ceil(sodSafety / Math.max(0.001, aotrDesign)));
    const rMix = 8 * RESERVOIR_MEAN_DEPTH;            // 8xH for shallow reservoirs
    const areaPerMod = Math.PI * rMix * rMix * 0.85;
    const effArea = EFFECTIVE_AREA_HA * 1e4;
    const modsByCov = Math.max(1, Math.ceil((effArea * target / 100) / areaPerMod));
    // AWWA M37 (2011) cap at 6/ha
    const awwaMax = Math.ceil(RESERVOIR_AREA_HA * 6);
    const awwaMin = Math.max(2, Math.ceil(RESERVOIR_AREA_HA * 1));
    const stricter = Math.max(modsBySOD, modsByCov);
    const rec = Math.min(awwaMax, Math.max(awwaMin, stricter));
    const recSpacing = Math.sqrt(effArea / rec);
    const minSpacing = Math.max(5, 2 * r_surface);
    const maxSpacing = 2 * rMix;
    const totalAirflow = rec * qPerModule;
    const totalAOTR = rec * aotrDesign;          // use design AOTR for system total
    const surplus = totalAOTR - totalSOD;
    const expectedCov = Math.min(100, rec * areaPerMod / effArea * 100);
    const totalKw = rec * kwPerMod;

    // Distribute modules over zones
    const perZone = Math.max(1, Math.ceil(rec / ZONE_DEFS.length));
    const modulePts: { x: number; y: number }[] = [];
    for (const z of ZONE_DEFS) {
      const cols = Math.ceil(Math.sqrt(perZone));
      for (let i = 0; i < perZone && modulePts.length < rec; i++) {
        const row = Math.floor(i / cols);
        const col = i % cols;
        modulePts.push({ x: z.cx + (col - cols / 2) * 6, y: z.cy + (row - cols / 2) * 5 });
      }
    }
    const arrayLabels = ZONE_DEFS.map(z => ({
      id: z.id, x: z.cx, y: z.cy, depthM: z.depthM, modulesAssigned: perZone,
    }));

    // ─── ADVANCED PHYSICS (v45) ─────────────────────────────────────────
    // DBM — McGinnis & Little 2002
    const D_O2 = 2.10e-9 * (T + 273.15) / 293.15;
    const t_rise = effSubm / Math.max(0.01, v_rise);
    const d_b_m = d_b / 1000;
    const A_b = Math.PI * d_b_m ** 2;
    const V_b = (Math.PI / 6) * d_b_m ** 3;
    // Higbie k_L
    const k_L = 2 * Math.sqrt(D_O2 / (Math.PI * Math.max(0.001, d_b_m / Math.max(0.01, v_rise))));
    const St_O2 = (k_L * A_b * t_rise) / Math.max(1e-15, V_b);
    const f_O2_DBM = 1 - Math.exp(-St_O2 * 0.05);
    const SOTE_DBM_pct = f_O2_DBM * 100 * 0.21;
    const SOTE_empirical_pct = soteM * effSubm;
    const P_bed = 101325 + rhoW(T) * 9.81 * effSubm;
    const d_b_surf_DBM = d_b * Math.pow((P_bed / 101325) * (1 - f_O2_DBM * 0.21), 1/3);

    // Lake / Wedderburn (Imberger 1985)
    const q_actBed = (qPerModule / 3600) * (101325 / P_bed);
    const A_plume = Math.PI * r_surface * r_surface * 9;       // scaled approximation
    const P_plume_W_m2 = (rhoW(T) * 9.81 * q_actBed * effSubm) / Math.max(0.01, A_plume);
    const E_strat_per_m2 = rhoW(T) * 9.81 * 1e-3 * effSubm * effSubm / 2;
    const wedderburnLike = P_plume_W_m2 * 3600 / Math.max(1, E_strat_per_m2);

    // HOD / Trophic status (Cornett & Rigler 1979)
    const trophicStatus = sodRate < 0.25 ? 'oligotrophic'
                        : sodRate < 0.5 ? 'mesotrophic'
                        : sodRate < 1.0 ? 'eutrophic'
                        : 'hyper-eutrophic';
    const z_hypo_m = 2.5;
    const HOD_mg_L_d = sodRate / z_hypo_m;
    const days_to_anoxia = Math.max(0.5, doSat(T) - 1.0) / Math.max(0.01, HOD_mg_L_d);

    // Algal-bloom suppression (Welch & Cooke 1995, Visser 1996, Reynolds 1984)
    const V_affected = A_plume * effSubm;
    const epsilon_W_kg = (P_plume_W_m2 * A_plume) / (rhoW(T) * Math.max(0.001, V_affected));
    const epsilon_threshold = 1e-6;
    const bloomSuppressionOk = epsilon_W_kg > epsilon_threshold && effSubm > 2.0;
    const tau_mix_hours = V_affected / Math.max(0.001, q_actBed * 100) / 3600;
    const doubling_time_h = 36;
    const mixingFasterThanGrowth = tau_mix_hours < doubling_time_h;

    return {
      nUnits, poresEach, poresTotal, qPerModule, d_b, v_rise, soteM, effSubm,
      sotrPerMod, aotrPerMod, aotrDesign, kwPerMod, r_diff, r_inf_bed, r_surface,
      sodRate, safetyF, target, totalSOD, sodSafety, modsBySOD, rMix, areaPerMod,
      effArea, modsByCov, stricter, awwaMax, awwaMin, rec, recSpacing, minSpacing, maxSpacing,
      totalAirflow, totalAOTR, surplus, expectedCov, totalKw, modulePts, arrayLabels,
      // v45 advanced physics
      SOTE_DBM_pct, SOTE_empirical_pct, d_b_surf_DBM, f_O2_DBM,
      P_plume_W_m2, E_strat_per_m2, wedderburnLike,
      trophicStatus, HOD_mg_L_d, days_to_anoxia,
      epsilon_W_kg, epsilon_threshold, bloomSuppressionOk,
      tau_mix_hours, doubling_time_h, mixingFasterThanGrowth,
    };
  }, [spec, profile]);

  // Validation checks
  const checks = useMemo(() => [
    { lbl: 'Bubble diameter 1-5 mm',                pass: computed.d_b >= 1.0 && computed.d_b <= 5.0 },
    { lbl: 'Rise velocity 0.10-0.40 m/s',           pass: computed.v_rise >= 0.10 && computed.v_rise <= 0.40 },
    { lbl: 'SOTE/m >= 2.0 %',                       pass: computed.soteM >= 2.0 },
    { lbl: 'Effective submergence >= 1.5 m',        pass: computed.effSubm >= 1.5 },
    { lbl: 'Per-module AOTR > 0.01 kg O2/h',        pass: computed.aotrPerMod > 0.01 },
    { lbl: 'Per-module power 0-50 kW',              pass: computed.kwPerMod > 0 && computed.kwPerMod < 50 },
    { lbl: 'Aeration efficiency >= 0.5 kg O2/kWh',  pass: computed.aotrPerMod / Math.max(0.001, computed.kwPerMod) >= 0.5 },
    { lbl: 'Total AOTR >= SOD demand',              pass: computed.totalAOTR >= computed.totalSOD },
    { lbl: 'Total AOTR >= SOD x safety',             pass: computed.totalAOTR >= computed.sodSafety },
    { lbl: 'Coverage >= 65%',                       pass: computed.expectedCov >= 65 },
    { lbl: 'Modules/ha 1-20 (AWWA range)',          pass: (computed.rec / RESERVOIR_AREA_HA) >= 1 && (computed.rec / RESERVOIR_AREA_HA) <= 20 },
    { lbl: 'Spacing within bounds',                  pass: computed.recSpacing >= computed.minSpacing && computed.recSpacing <= computed.maxSpacing },
    { lbl: 'Pore size 40-300 um',                    pass: spec.poreSizeUm >= 40 && spec.poreSizeUm <= 300 },
    { lbl: 'Mounting height 0.30-0.80 m',           pass: spec.mountingHeightM >= 0.30 && spec.mountingHeightM <= 0.80 },
    // v45 advanced checks
    { lbl: 'DBM/Stenstrom SOTE agreement <50% (McGinnis 2002)',
      pass: Math.abs(computed.SOTE_DBM_pct - computed.SOTE_empirical_pct) /
            Math.max(0.1, computed.SOTE_empirical_pct) < 0.5 },
    { lbl: 'Wedderburn-like > 1 (destrat capability, Imberger 1985)',
      pass: computed.wedderburnLike > 1.0 },
    { lbl: 'Days to hypolimnion anoxia > 30 (Cornett & Rigler 1979)',
      pass: computed.days_to_anoxia > 30 },
    { lbl: 'Mixing epsilon > 1e-6 W/kg (bloom suppression, Reynolds 1984)',
      pass: computed.epsilon_W_kg > computed.epsilon_threshold },
  ], [computed, spec]);

  const passes = checks.filter(c => c.pass).length;

  // ─── PDF download ──────────────────────────────────────────────────────
  const downloadMasterPDF = () => {
    const reasons = [
      `SOD demand for ${RESERVOIR_AREA_HA} ha at ${profile.sodRateG_m2_d} g/m^2/d gives ${computed.totalSOD.toFixed(2)} kg O2/h total demand.`,
      `Sizing uses DESIGN AOTR at 4 mg/L target working DO of ${computed.aotrDesign.toFixed(3)} kg O2/h per module (Metcalf & Eddy 2014 sizing convention). Required modules = ceil(${computed.sodSafety.toFixed(2)} / ${computed.aotrDesign.toFixed(3)}) = ${computed.modsBySOD}.`,
      `Coverage requires r_mix = 8 x H = ${computed.rMix.toFixed(1)} m mixing radius (shallow reservoir). Area/module = ${computed.areaPerMod.toFixed(0)} m^2. Required for 70% coverage = ${computed.modsByCov} modules.`,
      `Stricter = max(${computed.modsBySOD}, ${computed.modsByCov}) = ${computed.stricter} modules. Then AWWA M37 (2011) cap of 6/ha = ${computed.awwaMax} applied. Final recommended = ${computed.rec} modules at ${computed.recSpacing.toFixed(1)} m spacing.`,
    ];

    generateMasterReservoirReportPDF({
      layout: {
        spec: { ...spec },
        reservoir: { areaHa: RESERVOIR_AREA_HA, meanDepth: RESERVOIR_MEAN_DEPTH,
          volumeM3: RESERVOIR_VOL_M3, effAreaHa: EFFECTIVE_AREA_HA },
        layout: {
          currentSpacing: 30, currentMinDepth: 2.0,
          currentModuleCount: computed.rec, currentCoverage: computed.expectedCov,
          sodRate: computed.sodRate, targetCoverage: computed.target,
        },
        recommendations: {
          bestCount: computed.rec, bestSpacing: computed.recSpacing,
          bestHexSpacing: computed.recSpacing * 1.0746,
          modulesPerHa: computed.rec / RESERVOIR_AREA_HA,
          rMix: computed.rMix, areaPerMod: computed.areaPerMod,
          expectedCoverage: computed.expectedCov,
          totalAOTR: computed.totalAOTR, totalAirflow: computed.totalAirflow,
          totalKw: computed.totalKw, o2Surplus: computed.surplus,
          sodDemand: computed.totalSOD, sodWithSafety: computed.sodSafety,
          reasons,
        },
        arrayLabels: computed.arrayLabels, modules: computed.modulePts,
      },
      array: {
        spec: { ...spec },
        config: {
          moduleCount: 6, cols: 2, rows: 3,
          gapXM: 2.0, gapYM: 2.0,
          arrayWidthM: 2 * FRAME_SIZE + 2.0,
          arrayHeightM: 3 * FRAME_SIZE + 2 * 2.0,
          reservoirAreaHa: RESERVOIR_AREA_HA,
        },
        physics: {
          poresPerUnit: computed.poresEach, unitsPerModule: computed.nUnits,
          poresPerModule: computed.poresTotal, qPerModule: computed.qPerModule,
          d_b: computed.d_b, v_rise: computed.v_rise, soteM: computed.soteM,
          sotrPerModule: computed.sotrPerMod, aotrPerModule: computed.aotrPerMod,
          kwPerModule: computed.kwPerMod,
          r_inf_bed: computed.r_inf_bed, r_surface: computed.r_surface,
          effSubmM: computed.effSubm,
        },
        totals: {
          diffusers: 6 * computed.nUnits, airflow: 6 * computed.qPerModule,
          sotr: 6 * computed.sotrPerMod, aotr: 6 * computed.aotrPerMod,
          kw: 6 * computed.kwPerMod,
        },
      },
      validation: {
        spec: { ...spec },
        reservoir: { areaHa: RESERVOIR_AREA_HA, areaM2: RESERVOIR_AREA_HA * 1e4,
          meanDepth: RESERVOIR_MEAN_DEPTH, volumeM3: RESERVOIR_VOL_M3,
          shallow_m2: 5000, medium_m2: 18000, deep_m2: 7000, deepest_m2: 4000 },
        physics: {
          T: spec.waterTempC, rho: rhoW(spec.waterTempC), mu: muW(spec.waterTempC),
          nu: nuW(spec.waterTempC), sigma: sigmaW(spec.waterTempC), dosat: doSat(spec.waterTempC),
          poresEach: computed.poresEach, poresTotal: computed.poresTotal,
          qPerDiff: spec.airflowPerDiffNm3h, qPerModule: computed.qPerModule,
          d_b: computed.d_b, v_rise: computed.v_rise, soteM: computed.soteM,
          effSubm: computed.effSubm, sote_total: computed.soteM * computed.effSubm,
          sotr: computed.sotrPerMod, aotr: computed.aotrPerMod, kw: computed.kwPerMod,
          r_inf_bed: computed.r_inf_bed, r_surface: computed.r_surface,
          r_module: (FRAME_SIZE / Math.SQRT2) + computed.r_inf_bed,
          SAE: computed.aotrPerMod / Math.max(0.001, computed.kwPerMod),
        },
        optimization: {
          totalSOD: computed.totalSOD, sodSafety: computed.sodSafety,
          modsBySOD: computed.modsBySOD, rMix: computed.rMix,
          areaPerMod: computed.areaPerMod, effArea: computed.effArea,
          modsByCov: computed.modsByCov, rec: computed.rec,
          recSpacing: computed.recSpacing, minSpacing: computed.minSpacing,
          maxSpacing: computed.maxSpacing, totalAirflow: computed.totalAirflow,
          totalAOTR: computed.totalAOTR, surplus: computed.surplus,
          expectedCov: computed.expectedCov, totalKw: computed.totalKw,
        },
        arrayLabels: computed.arrayLabels, modules: computed.modulePts,
        checks, sodRate: computed.sodRate, safetyFactor: computed.safetyF,
        targetCoverage: computed.target,
      },
      preDredge: {
        spec: { ...spec },
        reservoirAreaHa: RESERVOIR_AREA_HA, baseWaterDepthM: 3.5,
        arrays: computed.arrayLabels.map(a => ({
          id: a.id, zoneLabel: a.id, depthMid: a.depthM,
          cx: a.x, cy: a.y, moduleCount: a.modulesAssigned,
        })),
      },
      postDredge: {
        spec: { ...spec },
        reservoirAreaHa: RESERVOIR_AREA_HA, baseWaterDepthM: 3.5,
        arrays: computed.arrayLabels.map(a => ({
          id: a.id, zoneLabel: a.id, depthMid: a.depthM,
          cx: a.x, cy: a.y, moduleCount: a.modulesAssigned,
        })),
      },
      advanced: {
        SOTE_DBM_pct: computed.SOTE_DBM_pct,
        SOTE_empirical_pct: computed.SOTE_empirical_pct,
        d_b_surf_DBM: computed.d_b_surf_DBM,
        f_O2_DBM: computed.f_O2_DBM,
        P_plume_W_m2: computed.P_plume_W_m2,
        E_strat_per_m2: computed.E_strat_per_m2,
        wedderburnLike: computed.wedderburnLike,
        trophicStatus: computed.trophicStatus,
        HOD_mg_L_d: computed.HOD_mg_L_d,
        days_to_anoxia: computed.days_to_anoxia,
        epsilon_W_kg: computed.epsilon_W_kg,
        epsilon_threshold: computed.epsilon_threshold,
        bloomSuppressionOk: computed.bloomSuppressionOk,
        tau_mix_hours: computed.tau_mix_hours,
        doubling_time_h: computed.doubling_time_h,
        mixingFasterThanGrowth: computed.mixingFasterThanGrowth,
      },
    }, profile);
    toast.success(`Master PDF downloaded (${profile.label})`);
  };

  const fmt = (v: number, dp = 2) => isNaN(v) || !isFinite(v) ? '—' : v.toFixed(dp);
  const isTube = spec.diffuserType === 'hdpe_porous_tube' || spec.diffuserType === 'epdm_porous_tube';

  return (
    <div className="flex flex-col h-full overflow-auto">
      <PageHeader
        title="Reservoir Master Report"
        subtitle="Consolidated engineering report - data from all reservoir-related pages"
        icon={FileText}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={downloadMasterPDF}
              className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg bg-rb-brand text-slate-900 hover:bg-rb-brand/90 transition-all font-bold shadow-lg shadow-amber-900/30">
              <Download className="w-4 h-4" /> Download Master PDF
            </button>
            <button onClick={() => navigate('/reservoir-layout')}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200">
              <ArrowLeft className="w-3.5 h-3.5" /> Layout
            </button>
          </div>
        }
      />

      <div className="flex-1 overflow-auto p-4 max-w-5xl mx-auto w-full space-y-4">

        {/* Seasonal profile selector */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-2 flex items-center gap-2">
            <Activity className="w-4 h-4 text-rb-brand" /> Seasonal Water Profile
          </h2>
          <p className="text-[11px] text-slate-400 mb-3">
            Selecting a profile cascades into all calculations and the PDF report.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {SEASONAL_PROFILES.map(p => (
              <button key={p.id} onClick={() => setSeasonId(p.id)}
                className={`text-left rounded-lg p-2.5 border-2 transition-all ${
                  seasonId === p.id
                    ? 'bg-rb-brand/15 border-rb-brand/40'
                    : 'border-slate-700/40 hover:border-slate-600 bg-slate-800/20'
                }`}>
                <div className="text-[10px] font-bold text-slate-100 mb-0.5">{p.label}</div>
                <div className="text-[9px] text-slate-400 grid grid-cols-2 gap-x-2 leading-tight">
                  <div>T:</div><div className="text-slate-200 font-mono">{p.waterTempC}°C</div>
                  <div>DO₀:</div><div className="text-slate-200 font-mono">{p.backgroundDOMgL}</div>
                  <div>SOD:</div><div className="text-slate-200 font-mono">{p.sodRateG_m2_d}</div>
                  <div>α:</div><div className="text-slate-200 font-mono">{p.alphaFactor}</div>
                </div>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-rb-brand mt-2">
            Active: <strong>{profile.label}</strong> — {profile.desc}
          </p>
        </div>

        {/* Executive summary */}
        <div className="rb-card p-5 border-2 border-rb-brand/30 bg-gradient-to-br from-rb-brand/5 to-transparent">
          <h2 className="text-base font-bold text-slate-100 mb-3 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-rb-brand" /> Executive Summary
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { lbl: 'Reservoir',  v: `${RESERVOIR_AREA_HA} ha`, sub: `${RESERVOIR_MEAN_DEPTH} m mean` },
              { lbl: 'Diffuser',   v: isTube ? 'TUBE' : 'DISC',  sub: spec.diffuserType.split('_').pop() },
              { lbl: 'Modules',    v: `${computed.rec}`,         sub: `${(computed.rec / RESERVOIR_AREA_HA).toFixed(1)}/ha` },
              { lbl: 'Spacing',    v: `${fmt(computed.recSpacing, 0)} m`, sub: 'square grid' },
              { lbl: 'AOTR',       v: `${fmt(computed.totalAOTR, 2)}`, sub: 'kg O₂/h' },
              { lbl: 'Power',      v: `${fmt(computed.totalKw, 1)} kW`, sub: `${fmt(computed.totalKw / RESERVOIR_AREA_HA, 2)} kW/ha` },
              { lbl: 'Coverage',   v: `${fmt(computed.expectedCov, 0)}%`, sub: 'of effective area' },
              { lbl: 'Validation', v: `${passes}/${checks.length}`, sub: 'checks pass' },
            ].map(k => (
              <div key={k.lbl} className="bg-slate-900/40 border border-slate-700/40 rounded-xl p-3">
                <div className="text-[10px] text-slate-500 uppercase">{k.lbl}</div>
                <div className="text-lg font-bold text-rb-brand">{k.v}</div>
                <div className="text-[10px] text-slate-600">{k.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Page data sources */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            <Layers className="w-4 h-4 text-rb-brand" /> Page Data Sources (cross-checked)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { p: 'Diffuser Module',     k: 'spec + tube parameters', i: Wind, c: 'text-violet-400' },
              { p: 'Module Array',        k: 'array configuration',     i: Layers, c: 'text-cyan-400' },
              { p: 'Reservoir Layout',    k: 'array placement + recommendations', i: MapPin, c: 'text-emerald-400' },
              { p: 'Sediment Bed Physics',k: 'bed type + plume interaction', i: Mountain, c: 'text-amber-400' },
              { p: 'Reservoir Validation',k: '19 independent pass/fail checks', i: Shield, c: 'text-rose-400' },
              { p: 'Calculation Steps',   k: 'full physics walkthrough', i: BarChart3, c: 'text-sky-400' },
            ].map(s => (
              <div key={s.p} className="flex items-center gap-2 p-2 rounded-lg bg-slate-800/40">
                <s.i className={`w-4 h-4 ${s.c} shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold text-slate-200">{s.p}</div>
                  <div className="text-[10px] text-slate-500">{s.k}</div>
                </div>
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              </div>
            ))}
          </div>
        </div>

        {/* Advanced Physics Snapshot (v45) */}
        <div className="rb-card p-4 border-cyan-500/30">
          <h2 className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-400" /> Advanced Physics Snapshot (current session)
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div className="bg-slate-900/40 border border-slate-700/40 rounded-xl p-3">
              <div className="text-[10px] text-slate-500 uppercase">DBM SOTE</div>
              <div className="text-base font-bold text-cyan-300">{fmt(computed.SOTE_DBM_pct, 1)}%</div>
              <div className="text-[9px] text-slate-600">vs Stenstrom {fmt(computed.SOTE_empirical_pct, 1)}%</div>
            </div>
            <div className="bg-slate-900/40 border border-slate-700/40 rounded-xl p-3">
              <div className="text-[10px] text-slate-500 uppercase">Wedderburn-like</div>
              <div className={`text-base font-bold ${computed.wedderburnLike > 1 ? 'text-emerald-400' : 'text-amber-400'}`}>
                {fmt(computed.wedderburnLike, 2)}
              </div>
              <div className="text-[9px] text-slate-600">&gt; 1 = destrat OK</div>
            </div>
            <div className="bg-slate-900/40 border border-slate-700/40 rounded-xl p-3">
              <div className="text-[10px] text-slate-500 uppercase">Days to anoxia</div>
              <div className={`text-base font-bold ${computed.days_to_anoxia > 30 ? 'text-emerald-400' : 'text-amber-400'}`}>
                {fmt(computed.days_to_anoxia, 0)}
              </div>
              <div className="text-[9px] text-slate-600">{computed.trophicStatus}</div>
            </div>
            <div className="bg-slate-900/40 border border-slate-700/40 rounded-xl p-3">
              <div className="text-[10px] text-slate-500 uppercase">Mixing ε (W/kg)</div>
              <div className={`text-base font-bold ${computed.epsilon_W_kg > computed.epsilon_threshold ? 'text-emerald-400' : 'text-amber-400'}`}>
                {computed.epsilon_W_kg.toExponential(1)}
              </div>
              <div className="text-[9px] text-slate-600">bloom suppression</div>
            </div>
          </div>
          <p className="text-[10px] text-slate-400 leading-relaxed">
            DBM (McGinnis &amp; Little 2002) cross-validates the Stenstrom &amp; Rosso empirical SOTE.{' '}
            Wedderburn-like (Imberger 1985) checks destratification energy balance.{' '}
            Days-to-anoxia (Cornett &amp; Rigler 1979) gives the trophic-status context.{' '}
            Mixing ε (Reynolds 1984) is the bloom-suppression threshold.
          </p>
        </div>

        {/* What's in the PDF */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4 text-rb-brand" /> Master PDF Contents (10 parts)
          </h2>
          <ol className="space-y-1 text-[11px] text-slate-300 list-decimal list-inside leading-relaxed">
            <li><strong>Part A —</strong> Assumptions, seasonal profile, equipment specification (tube/disc geometry)</li>
            <li><strong>Part B —</strong> Calculation hierarchy: Diffuser → Module → Array → System (4-level breakdown)</li>
            <li><strong>Part C —</strong> Reservoir geometry, plan view (20 m grid) and array placement table</li>
            <li><strong>Part D —</strong> Bed physics with recommended settings</li>
            <li><strong>Part E —</strong> Pre-dredging scenario summary + cross-section visual</li>
            <li><strong>Part F —</strong> Post-dredging scenario summary + cross-section visual</li>
            <li><strong>Part G —</strong> Validation summary (independent pass/fail checks)</li>
            <li><strong>Part H —</strong> Bill of materials + operational cost</li>
            <li><strong>NEW Part J —</strong> Advanced physics &amp; long-term outcomes (DBM, Lake Number, HOD, bloom suppression, Beutel-Horne meta-review)</li>
            <li><strong>Part I —</strong> Literature references (35+ peer-reviewed sources)</li>
          </ol>
        </div>

        {/* Validation summary */}
        <div className="rb-card p-4">
          <h2 className="text-sm font-bold text-slate-100 mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-rb-brand" /> Validation Snapshot ({passes}/{checks.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {checks.map((c, i) => (
              <div key={i} className={`flex items-center gap-2 p-2 rounded text-[11px] ${
                c.pass ? 'bg-emerald-500/5 text-slate-300' : 'bg-amber-500/5 text-slate-300'
              }`}>
                <span className={c.pass ? 'text-emerald-400' : 'text-amber-400'}>
                  {c.pass ? '✓' : '!'}
                </span>
                <span>{c.lbl}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Big download CTA at the bottom */}
        <div className="rb-card p-5 text-center border-2 border-rb-brand/30">
          <p className="text-sm text-slate-400 mb-3">
            Generate the complete consolidated PDF report — all data from every page, ready for the customer.
          </p>
          <button onClick={downloadMasterPDF}
            className="px-6 py-3 rounded-xl bg-rb-brand text-slate-900 font-bold text-sm hover:bg-rb-brand/90 transition-all shadow-xl shadow-amber-900/30 inline-flex items-center gap-2">
            <Download className="w-5 h-5" /> Download Master Reservoir PDF Report
          </button>
        </div>
      </div>
    </div>
  );
}
